/**
 * Demo Phase 2 & Phase 3 Panels
 * 
 * Force-renders Phase 2 (Post-Op Reconciliation) and Phase 3 (Revenue Insights) panels
 * in demo mode, regardless of case state. These panels are pre-populated with demo data
 * and are entirely read-only.
 * 
 * CRITICAL GUARDRAILS (NON-NEGOTIABLE):
 * - Demo overrides apply ONLY to UI visibility and demo data
 * - Do NOT write demo data into production tables
 * - Do NOT alter case status permanently
 * - Do NOT enable Phase 3 logic execution
 * - Demo panels must not block export or progression
 * 
 * HARD BOUNDARIES (ENFORCED IN ALL MODES):
 * - LINK does NOT generate clinical content
 * - LINK does NOT suggest performing additional procedures
 * - LINK does NOT add CPTs not in authorized bundle
 * - LINK does NOT submit claims (export only)
 * - Surgeon is sole clinical authority
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  CheckCircle2,
  FileText,
  Lock,
  Sparkles,
  Eye,
  ShieldCheck,
  Info,
  ChevronDown,
  ChevronUp,
  History,
  Shield,
  Fingerprint,
  AlertTriangle,
} from 'lucide-react';
import { 
  DEMO_OP_NOTE_TEXT, 
  DEMO_RECONCILIATION_ENTRIES,
  DEMO_CLARIFICATION_QUESTIONS,
  DEMO_PHASE3_INSIGHTS,
} from '@/lib/demo/phase2DemoData';
import { DemoDataBadge } from '@/components/ui/DemoDataBadge';
import { DemoModeInlineNote } from '@/components/ui/DemoModeInlineNote';
import { useDemoPath, useDemoPathVisibility } from '@/contexts/DemoPathContext';
import { cn } from '@/lib/utils';

interface DemoPhase2Phase3PanelsProps {
  authorizedCpts?: string[];
  className?: string;
}

/**
 * Demo Post-Op Documentation Panel (Phase 2A)
 * Shows pre-filled op note text
 */
function DemoOpNotePanel() {
  return (
    <Card className="border-success/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-success" />
            Post-Op Documentation
            <DemoDataBadge />
          </CardTitle>
          <Badge variant="outline" className="gap-1 text-success border-success/50">
            <Lock className="w-3 h-3" />
            Immutable
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Alert className="border-success/30 bg-success/5">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <AlertTitle className="text-success">Documentation Received</AlertTitle>
          <AlertDescription className="text-xs">
            Source: <span className="font-medium uppercase">PASTE</span>
          </AlertDescription>
        </Alert>

        <div className="bg-muted/30 border rounded-lg p-3 max-h-48 overflow-y-auto">
          <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide font-medium">
            Original Content (Read-Only)
          </p>
          <pre className="text-xs whitespace-pre-wrap font-mono text-foreground/80">
            {DEMO_OP_NOTE_TEXT.slice(0, 800)}...
          </pre>
        </div>

        <DemoModeInlineNote />
      </CardContent>
    </Card>
  );
}

/**
 * Demo Clarification Questions Panel (Phase 2B)
 * Shows pre-answered clarification questions
 */
function DemoClarificationPanel() {
  return (
    <Card className="border-success/50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CheckCircle2 className="w-5 h-5 text-success" />
          Clarification Complete
          <DemoDataBadge />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="border-success/50 bg-success/5">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <AlertTitle className="text-success">All Elements Confirmed</AlertTitle>
          <AlertDescription>
            Surgeon has confirmed all required documentation elements.
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          {Object.entries(
            DEMO_CLARIFICATION_QUESTIONS.reduce((acc, q) => {
              if (!acc[q.cptCode]) acc[q.cptCode] = [];
              acc[q.cptCode].push(q);
              return acc;
            }, {} as Record<string, typeof DEMO_CLARIFICATION_QUESTIONS>)
          ).map(([cptCode, questions]) => (
            <div key={cptCode} className="rounded-lg border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">
                    {cptCode}
                  </Badge>
                </div>
                <Badge variant="default" className="bg-success text-success-foreground">
                  {questions.length}/{questions.length} confirmed
                </Badge>
              </div>
              <div className="space-y-1">
                {questions.map((q, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    <span className="text-muted-foreground">{q.requiredElement}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DemoModeInlineNote />
      </CardContent>
    </Card>
  );
}

/**
 * Demo CPT Reconciliation Panel (Phase 2C)
 * Shows pre-confirmed CPT reconciliation entries
 */
function DemoReconciliationPanel() {
  return (
    <Card className="border-2 border-success/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="w-5 h-5 text-success" />
            CPT Reconciliation Status
            <DemoDataBadge />
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {DEMO_RECONCILIATION_ENTRIES.length} CPTs
            </Badge>
            <Badge variant="secondary" className="text-xs bg-success/10 text-success">
              {DEMO_RECONCILIATION_ENTRIES.length} Billable
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="border-success/50 bg-success/5">
          <CheckCircle2 className="w-4 h-4 text-success" />
          <AlertTitle className="text-success">Ready for Export</AlertTitle>
          <AlertDescription>
            All CPTs have been confirmed. {DEMO_RECONCILIATION_ENTRIES.length} procedure(s) are billable.
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 font-medium">CPT Code</th>
                <th className="text-left p-2 font-medium">Description</th>
                <th className="text-left p-2 font-medium">Status</th>
                <th className="text-left p-2 font-medium">Billable</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_RECONCILIATION_ENTRIES.map((entry) => (
                <tr key={entry.cptCode} className="bg-success/5 border-t">
                  <td className="p-2 font-mono font-medium">{entry.cptCode}</td>
                  <td className="p-2 text-muted-foreground">{entry.cptDescription}</td>
                  <td className="p-2">
                    <Badge className="bg-success/10 text-success border-success/30 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Confirmed
                    </Badge>
                  </td>
                  <td className="p-2">
                    <Badge className="bg-success text-success-foreground text-xs">Yes</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DemoModeInlineNote />
      </CardContent>
    </Card>
  );
}

/**
 * Demo Revenue Insights Panel (Phase 3)
 * Shows pre-computed historical insights (read-only)
 */
function DemoRevenueInsightsPanel({ defaultExpanded = false }: { defaultExpanded?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const insights = DEMO_PHASE3_INSIGHTS;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-dashed">
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                Revenue Integrity Insights
                <Badge variant="outline" className="text-xs font-normal">
                  Advisory
                </Badge>
                <DemoDataBadge />
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {insights.revenueFlags.length} insights
                </Badge>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <DemoDataBadge variant="banner" className="mb-3" />
            
            {/* Phase 3 Boundaries Disclaimer */}
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Advisory Notice:</strong> Insights are retrospective observations only. 
                LINK does not generate clinical content, suggest procedures, or submit claims. 
                Clinical decisions remain solely with the surgeon.
              </AlertDescription>
            </Alert>

            {insights.revenueFlags.map((flag) => (
              <div
                key={flag.id}
                className={cn(
                  "p-3 rounded-lg border-l-4 bg-secondary/30",
                  flag.severity === 'low' && "border-l-muted-foreground/50",
                  flag.severity === 'medium' && "border-l-warning/50"
                )}
              >
                <div className="flex items-start gap-3">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        {flag.type.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {flag.cptCode}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {flag.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Pattern Metrics Summary */}
            <Separator className="my-4" />
            
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold text-success">
                  {insights.patternSummary.reconciliationRate.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">Reconciliation Rate</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold text-primary">
                  {insights.patternSummary.documentationCompliance.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">Doc Compliance</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold text-success">
                  {insights.patternSummary.denialAvoidance.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">Denial Avoidance</p>
              </div>
            </div>

            <DemoModeInlineNote className="mt-3" />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Demo Audit Trail Panel (Compliance Path)
 * Shows immutable audit log preview
 */
function DemoAuditTrailPanel({ defaultExpanded = false }: { defaultExpanded?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const DEMO_AUDIT_ENTRIES = [
    { timestamp: '2026-02-06T10:32:15Z', actor: 'Dr. J. Larkin, M.D.', action: 'Attestation submitted', details: 'Primary CPT + 2 subset codes' },
    { timestamp: '2026-02-06T10:32:16Z', actor: 'LINK System', action: 'Authorization created', details: 'Case #LNK-2026-0847' },
    { timestamp: '2026-02-06T10:34:22Z', actor: 'LINK System', action: 'Payer submission initiated', details: 'Lane: Portal' },
    { timestamp: '2026-02-06T14:15:00Z', actor: 'LINK Ops', action: 'Post-op note received', details: 'Source: PASTE' },
    { timestamp: '2026-02-06T14:18:33Z', actor: 'Dr. J. Larkin, M.D.', action: 'Clarification confirmed', details: '3/3 elements verified' },
    { timestamp: '2026-02-06T14:18:45Z', actor: 'LINK System', action: 'Reconciliation complete', details: '3 CPTs billable' },
  ];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-primary/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <History className="w-4 h-4 text-primary" />
                Immutable Audit Trail
                <Badge variant="outline" className="text-xs font-normal border-primary/50 text-primary">
                  Compliance
                </Badge>
                <DemoDataBadge />
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {DEMO_AUDIT_ENTRIES.length} events
                </Badge>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <DemoDataBadge variant="banner" className="mb-3" />

            <div className="space-y-2">
              {DEMO_AUDIT_ENTRIES.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-muted/20 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{entry.action}</span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.actor} · {entry.details}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <DemoModeInlineNote className="mt-3" />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Demo Security Posture Panel (Compliance Path)
 * Shows PHI security cues
 */
function DemoSecurityPosturePanel({ defaultExpanded = false }: { defaultExpanded?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const SECURITY_FEATURES = [
    { icon: Shield, label: 'Role-Based Access Control', status: 'Enabled', detail: 'Surgeon, Link Ops, Admin roles enforced' },
    { icon: Fingerprint, label: 'Tenant Isolation', status: 'Active', detail: 'Client data segregation via RLS' },
    { icon: Lock, label: 'Immutable Logs', status: 'Configured', detail: 'Append-only audit trail' },
    { icon: ShieldCheck, label: 'PHI Masking', status: 'Enabled', detail: 'Automatic in non-clinical contexts' },
  ];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-primary/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Security Posture
                <Badge variant="outline" className="text-xs font-normal border-primary/50 text-primary">
                  PHI
                </Badge>
                <DemoDataBadge />
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-success text-success-foreground text-xs">
                  Compliant
                </Badge>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <DemoDataBadge variant="banner" className="mb-3" />

            <div className="grid gap-2">
              {SECURITY_FEATURES.map((feature, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/20">
                  <div className="flex items-center gap-2">
                    <feature.icon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{feature.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-success border-success/50">
                      {feature.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            <DemoModeInlineNote className="mt-3" />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Main wrapper that renders all demo Phase 2 & 3 panels
 * Visibility and expansion state is controlled by the current demo path
 */
export function DemoPhase2Phase3Panels({ className }: DemoPhase2Phase3PanelsProps) {
  const { demoPath, isSurgeonPath, isAdminPath, isCompliancePath } = useDemoPath();
  const { shouldExpandPhase2, shouldExpandPhase3, shouldExpandInsights } = useDemoPathVisibility();

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center gap-2 text-sm text-warning bg-warning/10 p-2 rounded-lg border border-warning/30">
        <Sparkles className="w-4 h-4" />
        <span className="font-medium">Demo Mode Active</span>
        <span className="text-xs text-warning/80">
          — Phase 2 & 3 panels shown with simulated data
        </span>
      </div>

      {/* Phase 2: Post-Op Reconciliation - Always show in Surgeon/Compliance, collapsed in Admin */}
      {(isSurgeonPath || isCompliancePath) && (
        <>
          <DemoOpNotePanel />
          <DemoClarificationPanel />
          <DemoReconciliationPanel />
        </>
      )}
      
      {isAdminPath && (
        <Collapsible defaultOpen={false}>
          <Card className="border-muted">
            <CollapsibleTrigger asChild>
              <CardHeader className="py-3 cursor-pointer hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    Post-Op Reconciliation (Phase 2)
                    <DemoDataBadge />
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">Collapsed</Badge>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <DemoOpNotePanel />
                <DemoClarificationPanel />
                <DemoReconciliationPanel />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Phase 3: Revenue Insights - Expanded in Admin/Compliance, collapsed in Surgeon */}
      <DemoRevenueInsightsPanel defaultExpanded={isAdminPath || isCompliancePath} />

      {/* Compliance-only panels */}
      {isCompliancePath && (
        <>
          <DemoAuditTrailPanel defaultExpanded={true} />
          <DemoSecurityPosturePanel defaultExpanded={true} />
        </>
      )}
    </div>
  );
}

export { 
  DemoOpNotePanel, 
  DemoClarificationPanel, 
  DemoReconciliationPanel, 
  DemoRevenueInsightsPanel,
  DemoAuditTrailPanel,
  DemoSecurityPosturePanel,
};
